/**
 * adaptListinfoConfig — DỊCH MỘT `listinfo` (cấp TRANG) SANG CẤU HÌNH của DataListManager.
 *
 * Mảnh cuối của bộ chuyển đổi listinfo -> data_list_config: `adaptListinfoFields.js` lo phần CỘT, file
 * này lo phần MÀN HÌNH. Cùng vai trò với `adaptLegacyReportConfig` bên báo cáo (reportinfo -> ReportFrame).
 *
 * Nhờ đó `/page/list/<code>` và `/page/voucher/<code>` dựng được giao diện từ listinfo có sẵn mà KHÔNG
 * phải tạo thêm bản ghi `data_list_config` cho từng công ty. Route cũ `/list/<code>` và `/voucher/<code>`
 * vẫn chạy InputBase, không đụng tới.
 *
 * File KHÔNG import gì — `adaptListinfoFields` được TIÊM vào — để test node chạy trên chính mã nguồn này.
 */

/** Chỉ nhận Boolean thật; chuỗi script của bộ cũ KHÔNG dịch được -> coi như không đặt. */
function boolOnly(v) {
	return v === true || v === 'true';
}

/** Cờ CẤM của bộ cũ (`not_add`…) -> cờ CHO PHÉP của bộ mới. Không khai = CHO PHÉP. */
function allowFrom(notFlag) {
	return !boolOnly(notFlag);
}

/**
 * Bộ script vòng đời: tên của listinfo -> tên của data_list_config.
 * Giữ NGUYÊN nội dung chuỗi; việc biên dịch do `parseDataListConfig` lo (nó gán đủ cả hai bộ tên tham
 * số, xem lifecycleHooks.HOOK_ARGS) nên script cũ chạy được nguyên si.
 */
const SCRIPT_MAP = {
	expression_check_save: 'checkBeforeSave',
	on_saved: 'onSaved',
	on_deleting: 'onDeleting',
	on_deleted: 'onDeleted',
	on_init: 'onInit',
	on_init_update: 'onInitUpdate',
	handle_cell_clicked: 'handleCellClicked',
};

/** `sort_by` của bộ cũ: "ngay_ct desc" / "ma_kh" / JSON `{"ngay_ct":-1}` -> sortOptions của bộ mới. */
function parseSort(sortBy) {
	if (!sortBy || typeof sortBy !== 'string') return null;
	const raw = sortBy.trim();
	if (!raw) return null;
	if (raw.charAt(0) === '{') {
		try {
			const obj = JSON.parse(raw);
			if (obj && typeof obj === 'object' && Object.keys(obj).length) return obj;
		} catch (e) { /* trông như JSON mà hỏng -> không đoán bừa */ }
		return null;
	}
	const parts = raw.split(/[\s,]+/).filter(Boolean);
	if (!parts.length) return null;
	const field = parts[0];
	const dir = String(parts[1] || '').toLowerCase();
	return { [field]: (dir === 'desc' || dir === '-1') ? -1 : 1 };
}

function adaptSortBy(sortBy) {
	const sort = parseSort(sortBy);
	return sort ? [{ label: 'Mặc định', sort }] : null;
}

/**
 * `default_condition` của bộ cũ là chuỗi: hoặc JSON, hoặc SCRIPT.
 * JSON -> `condition` (điều kiện cứng). Script -> `conditionScript` (parseDataListConfig sẽ chạy nó).
 * ĐOÁN BỪA ở đây là nguy hiểm: nhét script vào `condition` thì màn hình lọc bằng một object rác và
 * hiện sai dữ liệu mà không có lỗi nào.
 */
function adaptDefaultCondition(raw) {
	if (!raw || typeof raw !== 'string') return {};
	const s = raw.trim();
	if (!s) return {};
	if (s.charAt(0) === '{') {
		try {
			const obj = JSON.parse(s);
			if (obj && typeof obj === 'object') return { condition: obj };
		} catch (e) { /* trông như JSON mà hỏng -> coi là script, an toàn hơn */ }
	}
	return { conditionScript: s };
}

/**
 * Dịch cả listinfo sang cấu hình DataListManager.
 *
 * @param listinfo    bản ghi listinfo (đã lean/toObject)
 * @param deps        { adaptListinfoFields } — tiêm từ adaptListinfoFields.js
 * @param options     { detailFieldsByForm, pageType } — `detailFieldsByForm` là subFields của các lưới
 *                    chi tiết do bên gọi nạp trước (xem collectDetailFormCodes); `pageType` là loại
 *                    trang đang mở ('voucher' | 'list') để quyết định kích thước form.
 * @returns cấu hình THÔ, chưa biên dịch script. Bên gọi phải cho qua `parseDataListConfig`.
 */
function adaptListinfoConfig(listinfo, deps, options) {
	if (!listinfo || !listinfo.code) return null;
	const li = listinfo;
	const opts = options || {};
	const adaptFields = deps && deps.adaptListinfoFields;
	if (typeof adaptFields !== 'function') return null;
	const out_gridReport = Array.isArray(opts.gridReport) ? opts.gridReport : [];
	const resolveFormSize = (deps && deps.resolveFormSize) || ((pageType, declared) => declared || 'md');

	// `gridReport` gom kết quả đọc grid_configs của từng field để bên gọi cảnh báo ra console.
	const gridReport = [];
	const fields = adaptFields(li.fields || [], { gridReport }, deps);
	out_gridReport.length = 0;
	for (const r of gridReport) out_gridReport.push(r);

	// Gắn subFields cho từng lưới chi tiết.
	for (const f of fields) {
		if (f.type !== 'ObjectArray' || !f.detailForm) continue;
		const sub = (opts.detailFieldsByForm || {})[f.detailForm];
		if (sub && sub.length) f.subFields = sub;
	}

	const out = {
		// `moduleCode` là mã MÀN HÌNH. Ở đây màn hình sinh ra TỪ chính listinfo này nên lấy luôn mã của nó.
		moduleCode: li.code,
		code: li.code,
		// `listinfoCode` là mã HÀNH VI SERVER — khai TƯỜNG MINH chứ không dựa vào việc nó tình cờ trùng
		// `moduleCode`. Đây là thứ chọn phân quyền / danh sách trạng thái / hook sự kiện server / ghi sổ
		// cho màn hình. Nhiều listinfo dùng chung một `api_code` mà vẫn xử lý khác nhau — mã này phân biệt.
		listinfoCode: li.code,
		apiCode: li.api_code || li.model_code || li.code,
		title: li.title || li.code,
		fields,

		canAdd: allowFrom(li.not_add),
		canEdit: allowFrom(li.not_edit),
		canDelete: allowFrom(li.not_delete),
		canCopy: allowFrom(li.not_copy),

		defaultViewMode: 'table',
		// listinfo đã có bố cục riêng (thứ tự `stt`, `grid_configs`, `group`) — KHÔNG tự tách thêm
		// "Thông tin chung" / "Thông tin khác" nữa, đoán thêm là phá cấu trúc đang có.
		autoGroupMaster: false,
		// `/page/voucher/<code>` LUÔN mở form full: chứng từ luôn nhiều dữ liệu, khổ md vừa chật vừa phải
		// cuộn. Ép ở đây, ghi đè cả `form_size` khai trong listinfo.
		formSize: resolveFormSize(opts.pageType, li.form_size),
	};

	const cond = adaptDefaultCondition(li.default_condition);
	if (cond.condition) out.condition = cond.condition;
	if (cond.conditionScript) out.conditionScript = cond.conditionScript;

	const sortOptions = adaptSortBy(li.sort_by);
	if (sortOptions) out.sortOptions = sortOptions;

	if (boolOnly(li.search_required)) out.searchRequired = true;
	// CHIA ĐÔI MÀN HÌNH DANH SÁCH (bảng ở trên, chi tiết của dòng đang chọn ở dưới).
	//
	// `/page/voucher/<code>` thì LUÔN BẬT, cùng lý do với `formSize: 'full'`: chứng từ nào cũng có lưới
	// chi tiết và người dùng cần xem ngay dòng hàng mà không phải mở form. Cờ `show_view_tabs_on_grid`
	// của listinfo KHÔNG phải cờ này — ở bộ cũ nó quyết định có NẠP THÊM dữ liệu cho các tab hiện trên
	// lưới hay không (`InputBase.js:709`), nên rất nhiều chứng từ để `false`; lấy nó làm căn cứ tắt chia
	// đôi là tắt nhầm gần hết.
	if (opts.pageType === 'voucher') out.showDetailOnGrid = true;
	// Trang danh sách thì vẫn theo khai báo, và chỉ khi khai TƯỜNG MINH: `showDetailOnGrid` phân biệt ba
	// trạng thái, "chưa đặt" nghĩa là TỰ BẬT khi có lưới chi tiết (masterDetailSplit.shouldShowDetailPane).
	// Gán bừa `false` là giết mất tính năng tự bật cho mọi listinfo chưa khai cờ này.
	else if (li.show_view_tabs_on_grid === true) out.showDetailOnGrid = true;
	else if (li.show_view_tabs_on_grid === false) out.showDetailOnGrid = false;

	for (const [oldKey, newKey] of Object.entries(SCRIPT_MAP)) {
		const v = li[oldKey];
		if (typeof v === 'string' && v.trim()) out[newKey] = v;
	}

	const changeCodeForm = li.options && li.options.change_code_form;
	if (changeCodeForm) out.changeCodeForm = changeCodeForm;

	// FORM TÌM KIẾM RIÊNG: `search_form` là MÃ một listinfo khác chỉ chứa ô tìm kiếm. DataListManager tự
	// nạp nó (qua napCauHinhDanhSach) khi người dùng bấm Lọc — xem searchFormRules.js.
	if (typeof li.search_form === 'string' && li.search_form.trim()) out.searchForm = li.search_form.trim();
	// TAB CỦA TRANG XEM: giữ NGUYÊN VĂN (biểu thức `default_condition`/`default_data`, template
	// `content_data`) — DataView chạy chúng đúng phương ngữ ViewBase, xem viewTabsRules.js.
	if (Array.isArray(li.view_tabs) && li.view_tabs.length) {
		out.viewTabs = li.view_tabs.filter(t => t && t.code).map(t => {
			const o = { code: t.code, name: t.name || t.title || t.code };
			for (const k of ['default_condition', 'default_data', 'content_data', 'data_pickers']) if (t[k] !== undefined && t[k] !== null && t[k] !== '') o[k] = t[k];
			return o;
		});
	}
	return out;
}

/**
 * Mã các listinfo CON cần nạp thêm để dựng lưới chi tiết. Bên gọi nạp rồi truyền lại qua
 * `options.detailFieldsByForm`.
 */
function collectDetailFormCodes(listinfo, deps) {
	const adaptFields = deps && deps.adaptListinfoFields;
	if (!listinfo || typeof adaptFields !== 'function') return [];
	const codes = [];
	for (const f of adaptFields(listinfo.fields || [], {}, deps)) {
		if (f.type === 'ObjectArray' && f.detailForm && codes.indexOf(f.detailForm) < 0) codes.push(f.detailForm);
	}
	return codes;
}

/**
 * Những thứ của listinfo mà bộ mới CHƯA có chỗ chứa. Trả về danh sách tên khoá để cảnh báo ra console —
 * im lặng bỏ qua là người khai báo tưởng màn hình mới đủ chức năng rồi mới phát hiện thiếu.
 */
// `view_tabs` và `search_form` ĐÃ có chỗ chứa (viewTabs / searchForm) — không còn ở đây.
const UNSUPPORTED_KEYS = ['title2', 'get_other_fields', 'breadcrumbs'];
function unsupportedKeys(listinfo) {
	if (!listinfo) return [];
	return UNSUPPORTED_KEYS.filter(k => {
		const v = listinfo[k];
		if (Array.isArray(v)) return v.length > 0;
		return v !== undefined && v !== null && v !== '' && v !== false;
	});
}

export {
	SCRIPT_MAP, UNSUPPORTED_KEYS,
	boolOnly, allowFrom, parseSort, adaptSortBy, adaptDefaultCondition,
	adaptListinfoConfig, collectDetailFormCodes, unsupportedKeys,
};
